import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  FaTimes, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt,
  FaCalendarAlt, FaUsers, FaComment,
  FaEdit, FaSave, FaHistory, FaTag, FaPlus,
  FaWhatsapp
} from 'react-icons/fa';
import { getLeadSourceById, getStatusById, leadStatuses } from '../data/mockData';
import apiService from '../services/api';

// Simple notification function
const showNotification = (message, type = 'success') => {
  // You can implement a proper notification system here
  console.log(`${type}: ${message}`);
};

const SliderOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 15000;
  display: flex;
  justify-content: flex-end;
  animation: slideIn 0.3s ease-out;
  
  /* Prevent body scroll */
  overflow: hidden;
  
  @keyframes slideIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const SliderPanel = styled.div`
  width: clamp(600px, 70vw, 850px);
  max-width: 95vw;
  height: 100%;
  max-height: 100vh;
  background: #ffffff;
  color: #1a202c;
  overflow-y: auto;
  animation: slideInFromRight 0.3s ease-out;
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.2);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', system-ui, sans-serif;
  display: flex;
  flex-direction: column;
  
  /* Custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 transparent;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f7fafc;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
  }
  
  @keyframes slideInFromRight {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }

  /* Responsive width for different desktop resolutions */
  @media (min-width: 1920px) {
    width: clamp(750px, 70vw, 850px);
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    width: clamp(650px, 65vw, 750px);
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    width: clamp(600px, 60vw, 700px);
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    width: clamp(550px, 55vw, 650px);
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    width: clamp(500px, 50vw, 600px);
  }
  
  @media (max-height: 600px) {
    height: 100vh;
  }
  
  @media (max-width: 1023px) {
    width: 100vw;
    max-width: 100vw;
  }
`;

const Header = styled.div`
  padding: clamp(1rem, 2vw, 1.5rem) clamp(1.25rem, 3vw, 2rem);
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 2px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 200;
  backdrop-filter: blur(10px);
  flex-shrink: 0;
  min-height: clamp(3rem, 5vw, 5rem);
  
  /* Responsive padding for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 1.5rem 2rem;
    min-height: 5rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 1.25rem 1.75rem;
    min-height: 4.5rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 1.125rem 1.5rem;
    min-height: 4rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 1rem 1.25rem;
    min-height: 3.5rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 0.875rem 1rem;
    min-height: 3rem;
  }
  
  @media (max-width: 1023px) {
    padding: 1rem 1.25rem;
    min-height: 3.75rem;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: clamp(1rem, 1.5vw, 1.5rem);
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: clamp(0.5rem, 1vw, 0.75rem);
  color: #1a202c;
  letter-spacing: -0.5px;
  
  /* Responsive font size for different desktop resolutions */
  @media (min-width: 1920px) {
    font-size: 1.5rem;
    gap: 0.75rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    font-size: 1.375rem;
    gap: 0.625rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    font-size: 1.25rem;
    gap: 0.5rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    font-size: 1.125rem;
    gap: 0.5rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    font-size: 1rem;
    gap: 0.375rem;
  }
  
  @media (max-width: 1023px) {
    font-size: 1.25rem;
    gap: 0.5rem;
  }
`;

const CloseButton = styled.button`
  background: #ffffff;
  border: 2px solid #e2e8f0;
  color: #4a5568;
  width: clamp(2rem, 3vw, 2.75rem);
  height: clamp(2rem, 3vw, 2.75rem);
  border-radius: clamp(0.5rem, 1vw, 0.75rem);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #f7fafc;
    border-color: #cbd5e0;
    color: #2d3748;
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  /* Responsive dimensions for different desktop resolutions */
  @media (min-width: 1920px) {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.75rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.625rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.5rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    width: 1.875rem;
    height: 1.875rem;
    border-radius: 0.375rem;
  }
  
  @media (max-width: 1023px) {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.5rem;
  }
`;

const Content = styled.div`
  padding: clamp(1rem, 2vw, 1.5rem) clamp(1.25rem, 3vw, 2rem) clamp(1.5rem, 3vw, 2rem) clamp(1.25rem, 3vw, 2rem);
  flex: 1;
  overflow-y: auto;
  
  /* Responsive padding for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 1.5rem 2rem 2rem 2rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 1.25rem 1.75rem 1.75rem 1.75rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 1.125rem 1.5rem 1.5rem 1.5rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 1rem 1.25rem 1.25rem 1.25rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 0.875rem 1rem 1rem 1rem;
  }
  
  @media (max-width: 1023px) {
    padding: 1rem 1.25rem 1.5rem 1.25rem;
  }
`;

const Section = styled.div`
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: clamp(0.75rem, 1.5vw, 1rem);
  padding: clamp(1rem, 2vw, 1.5rem);
  margin-bottom: clamp(0.75rem, 1.5vw, 1.25rem);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
  
  &:last-child {
    margin-bottom: 0;
  }

  /* Responsive padding and margins for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 1.5rem;
    margin-bottom: 1.25rem;
    border-radius: 1rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 1.375rem;
    margin-bottom: 1.125rem;
    border-radius: 0.875rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 1.25rem;
    margin-bottom: 1rem;
    border-radius: 0.75rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 1.125rem;
    margin-bottom: 0.875rem;
    border-radius: 0.75rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 1rem;
    margin-bottom: 0.75rem;
    border-radius: 0.625rem;
  }
  
  @media (max-width: 1023px) {
    padding: 0.875rem;
    margin-bottom: 0.75rem;
    border-radius: 0.75rem;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 clamp(0.75rem, 1.5vw, 1.25rem) 0;
  font-size: clamp(0.875rem, 1.25vw, 1.25rem);
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: clamp(0.5rem, 1vw, 0.75rem);
  color: #4299e1;
  letter-spacing: -0.25px;
  
  /* Responsive font size and spacing for different desktop resolutions */
  @media (min-width: 1920px) {
    font-size: 1.25rem;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    font-size: 1.125rem;
    gap: 0.625rem;
    margin-bottom: 1.125rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    font-size: 1rem;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    font-size: 0.9375rem;
    gap: 0.5rem;
    margin-bottom: 0.875rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    font-size: 0.875rem;
    gap: 0.375rem;
    margin-bottom: 0.75rem;
  }
  
  @media (max-width: 1023px) {
    font-size: 1rem;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(clamp(12rem, 20vw, 18rem), 1fr));
  gap: clamp(0.75rem, 1.5vw, 1rem);

  /* Responsive grid for different desktop resolutions */
  @media (min-width: 1920px) {
    grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
    gap: 1rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
    gap: 0.875rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
    gap: 0.75rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
    gap: 0.625rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
    gap: 0.5rem;
  }

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const InfoItemRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: clamp(0.5rem, 1vw, 1rem);

  /* Responsive gap for different desktop resolutions */
  @media (min-width: 1920px) {
    gap: 1rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    gap: 0.875rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    gap: 0.75rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    gap: 0.625rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    gap: 0.5rem;
  }

  @media (max-width: 1023px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 48%;
  gap: clamp(0.25rem, 0.5vw, 0.375rem);
`;

const InfoLabel = styled.label`
  font-size: clamp(0.625rem, 0.75vw, 0.75rem);
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  /* Responsive font size for different desktop resolutions */
  @media (min-width: 1920px) {
    font-size: 0.75rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    font-size: 0.6875rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    font-size: 0.625rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    font-size: 0.625rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    font-size: 0.625rem;
  }
  
  @media (max-width: 1023px) {
    font-size: 0.6875rem;
  }
`;

const NameSpacing = styled.div`
  padding: clamp(0.5rem, 1vw, 0.625rem);
`;

const InfoValue = styled.div`
  font-size: clamp(0.75rem, 1vw, 0.9375rem);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: clamp(0.5rem, 1vw, 0.625rem);
  color: #1a202c;
  
  /* Responsive font size and gap for different desktop resolutions */
  @media (min-width: 1920px) {
    font-size: 0.9375rem;
    gap: 0.625rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    font-size: 0.875rem;
    gap: 0.5rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    font-size: 0.8125rem;
    gap: 0.5rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    font-size: 0.75rem;
    gap: 0.5rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    font-size: 0.75rem;
    gap: 0.375rem;
  }
  
  @media (max-width: 1023px) {
    font-size: 0.8125rem;
    gap: 0.5rem;
  }
`;

const EditableInput = styled.input`
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: clamp(0.375rem, 0.75vw, 0.625rem);
  color: #2d3748;
  padding: clamp(0.5rem, 1vw, 0.75rem) clamp(0.75rem, 1.5vw, 1rem);
  font-size: clamp(0.6875rem, 0.875vw, 0.875rem);
  width: 100%;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }
  
  &::placeholder {
    color: #a0aec0;
  }
  
  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    border-radius: 0.625rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 0.6875rem 0.875rem;
    font-size: 0.8125rem;
    border-radius: 0.5rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 0.625rem 0.75rem;
    font-size: 0.75rem;
    border-radius: 0.5rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 0.5rem 0.625rem;
    font-size: 0.6875rem;
    border-radius: 0.375rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 0.5rem 0.625rem;
    font-size: 0.6875rem;
    border-radius: 0.375rem;
  }
  
  @media (max-width: 1023px) {
    padding: 0.625rem 0.75rem;
    font-size: 0.75rem;
    border-radius: 0.5rem;
  }
`;

const EditableTextArea = styled.textarea`
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: clamp(0.375rem, 0.75vw, 0.625rem);
  color: #2d3748;
  padding: clamp(0.5rem, 1vw, 0.75rem) clamp(0.75rem, 1.5vw, 1rem);
  font-size: clamp(0.6875rem, 0.875vw, 0.875rem);
  width: 100%;
  min-height: clamp(4rem, 8vw, 6.25rem);
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }
  
  &::placeholder {
    color: #a0aec0;
  }
  
  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    border-radius: 0.625rem;
    min-height: 6.25rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 0.6875rem 0.875rem;
    font-size: 0.8125rem;
    border-radius: 0.5rem;
    min-height: 5.5rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 0.625rem 0.75rem;
    font-size: 0.75rem;
    border-radius: 0.5rem;
    min-height: 5rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 0.5rem 0.625rem;
    font-size: 0.6875rem;
    border-radius: 0.375rem;
    min-height: 4.5rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 0.5rem 0.625rem;
    font-size: 0.6875rem;
    border-radius: 0.375rem;
    min-height: 4rem;
  }
  
  @media (max-width: 1023px) {
    padding: 0.625rem 0.75rem;
    font-size: 0.75rem;
    border-radius: 0.5rem;
    min-height: 5rem;
  }
`;

const SourceBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: clamp(0.25rem, 0.5vw, 0.375rem);
  background: ${props => props.color || '#667eea'};
  color: white;
  padding: clamp(0.375rem, 0.75vw, 0.5rem) clamp(0.75rem, 1.5vw, 1rem);
  border-radius: clamp(0.75rem, 1.5vw, 1.25rem);
  font-size: clamp(0.625rem, 0.75vw, 0.8125rem);
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 0.5rem 1rem;
    font-size: 0.8125rem;
    border-radius: 1.25rem;
    gap: 0.375rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 0.4375rem 0.875rem;
    font-size: 0.75rem;
    border-radius: 1.125rem;
    gap: 0.375rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.6875rem;
    border-radius: 1rem;
    gap: 0.25rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 0.375rem 0.625rem;
    font-size: 0.625rem;
    border-radius: 0.875rem;
    gap: 0.25rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 0.375rem 0.625rem;
    font-size: 0.625rem;
    border-radius: 0.875rem;
    gap: 0.25rem;
  }
  
  @media (max-width: 1023px) {
    padding: 0.4375rem 0.75rem;
    font-size: 0.6875rem;
    border-radius: 1rem;
    gap: 0.375rem;
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: clamp(0.25rem, 0.5vw, 0.375rem);
  background: ${props => props.color || '#667eea'};
  color: white;
  padding: clamp(0.375rem, 0.75vw, 0.5rem) clamp(0.75rem, 1.5vw, 1rem);
  border-radius: clamp(0.75rem, 1.5vw, 1.25rem);
  font-size: clamp(0.625rem, 0.75vw, 0.8125rem);
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 0.5rem 1rem;
    font-size: 0.8125rem;
    border-radius: 1.25rem;
    gap: 0.375rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 0.4375rem 0.875rem;
    font-size: 0.75rem;
    border-radius: 1.125rem;
    gap: 0.375rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.6875rem;
    border-radius: 1rem;
    gap: 0.25rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 0.375rem 0.625rem;
    font-size: 0.625rem;
    border-radius: 0.875rem;
    gap: 0.25rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 0.375rem 0.625rem;
    font-size: 0.625rem;
    border-radius: 0.875rem;
    gap: 0.25rem;
  }
  
  @media (max-width: 1023px) {
    padding: 0.4375rem 0.75rem;
    font-size: 0.6875rem;
    border-radius: 1rem;
    gap: 0.375rem;
  }
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.375rem, 0.75vw, 0.5rem);
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: clamp(0.375rem, 0.75vw, 0.5rem) clamp(0.5rem, 1vw, 0.75rem);
  background: rgba(255, 255, 255, 0.1);
  border-radius: clamp(0.375rem, 0.75vw, 0.5rem);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  font-size: clamp(0.6875rem, 0.875vw, 0.875rem);
  gap: clamp(0.5rem, 1vw, 0.625rem);
`;

const WhatsappIcon = styled.a`
  font-size: 1.2rem;
  color: #25d366;
  margin-left: 0.2rem;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    color: #128c7e;
  }
`;

const PrimaryBadge = styled.span`
  background: #48bb78;
  color: white;
  padding: clamp(0.125rem, 0.25vw, 0.25rem) clamp(0.25rem, 0.5vw, 0.375rem);
  border-radius: clamp(0.375rem, 0.75vw, 0.625rem);
  font-size: clamp(0.5rem, 0.625vw, 0.625rem);
  font-weight: 500;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: clamp(0.5rem, 1vw, 0.75rem);
  margin-top: clamp(1rem, 2vw, 1.5rem);
`;

const ActionButton = styled.button`
  background: ${props => {
    switch (props.type) {
      case 'primary': return '#48bb78';
      case 'secondary': return '#667eea';
      case 'danger': return '#f56565';
      default: return '#f7fafc';
    }
  }};
  color: ${props => props.type ? 'white' : '#4a5568'};
  border: ${props => props.type ? 'none' : '2px solid #e2e8f0'};
  border-radius: clamp(0.5rem, 1vw, 0.75rem);
  padding: clamp(0.5rem, 1vw, 0.75rem) clamp(0.875rem, 1.5vw, 1.25rem);
  font-size: clamp(0.6875rem, 0.875vw, 0.875rem);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: clamp(0.375rem, 0.75vw, 0.5rem);
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${props => {
    switch (props.type) {
      case 'primary': return 'rgba(72, 187, 120, 0.3)';
      case 'secondary': return 'rgba(102, 126, 234, 0.3)';
      case 'danger': return 'rgba(245, 101, 101, 0.3)';
      default: return 'rgba(0, 0, 0, 0.1)';
    }
  }};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
    border-radius: 0.75rem;
    gap: 0.5rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 0.6875rem 1.125rem;
    font-size: 0.8125rem;
    border-radius: 0.625rem;
    gap: 0.4375rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 0.625rem 1rem;
    font-size: 0.75rem;
    border-radius: 0.5rem;
    gap: 0.375rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 0.5rem 0.875rem;
    font-size: 0.6875rem;
    border-radius: 0.5rem;
    gap: 0.375rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.6875rem;
    border-radius: 0.375rem;
    gap: 0.25rem;
  }
  
  @media (max-width: 1023px) {
    padding: 0.625rem 1rem;
    font-size: 0.75rem;
    border-radius: 0.5rem;
    gap: 0.375rem;
  }
`;

const Select = styled.select`
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: clamp(0.375rem, 0.75vw, 0.625rem);
  color: #2d3748;
  padding: clamp(0.5rem, 1vw, 0.75rem) clamp(0.75rem, 1.5vw, 1rem);
  font-size: clamp(0.6875rem, 0.875vw, 0.875rem);
  width: 70%;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }
  
  option {
    background: white;
    color: #2d3748;
    padding: clamp(0.375rem, 0.75vw, 0.5rem);
  }
  
  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    border-radius: 0.625rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 0.6875rem 0.875rem;
    font-size: 0.8125rem;
    border-radius: 0.5rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 0.625rem 0.75rem;
    font-size: 0.75rem;
    border-radius: 0.5rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 0.5rem 0.625rem;
    font-size: 0.6875rem;
    border-radius: 0.375rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 0.5rem 0.625rem;
    font-size: 0.6875rem;
    border-radius: 0.375rem;
  }
  
  @media (max-width: 1023px) {
    padding: 0.625rem 0.75rem;
    font-size: 0.75rem;
    border-radius: 0.5rem;
  }
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.75rem, 1.5vw, 1rem);
`;

const TimelineItem = styled.div`
  display: flex;
  gap: clamp(0.75rem, 1.5vw, 1rem);
  padding: clamp(1rem, 2vw, 1.25rem);
  background: #f8fafc;
  border-radius: clamp(0.5rem, 1vw, 0.75rem);
  border-left: 4px solid #4299e1;
  border: 1px solid #e2e8f0;
`;

const TimelineIcon = styled.div`
  width: clamp(2rem, 3vw, 2.75rem);
  height: clamp(2rem, 3vw, 2.75rem);
  background: #4299e1;
  border-radius: clamp(0.5rem, 1vw, 0.75rem);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(66, 153, 225, 0.2);
`;

const TimelineContent = styled.div`
  flex: 1;
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
`;

const TimelineAction = styled.h4`
  margin: 0;
  font-size: clamp(0.75rem, 1vw, 0.9375rem);
  font-weight: 600;
  color: #4299e1;
`;

const TimelineTime = styled.span`
  font-size: clamp(0.625rem, 0.75vw, 0.75rem);
  color: #64748b;
  font-weight: 500;
`;

const TimelineDescription = styled.p`
  margin: 0;
  font-size: clamp(0.6875rem, 0.875vw, 0.875rem);
  color: #2d3748;
  line-height: 1.5;
`;

const TimelineUser = styled.div`
  font-size: clamp(0.625rem, 0.75vw, 0.75rem);
  color: #64748b;
  margin-top: clamp(0.25rem, 0.5vw, 0.375rem);
  font-weight: 500;
`;

const CommentSection = styled.div`
  margin-top: clamp(1rem, 2vw, 1.25rem);
`;

const CommentItem = styled.div`
  background: #f0fff4;
  border-radius: clamp(0.5rem, 1vw, 0.75rem);
  padding: clamp(0.875rem, 1.5vw, 1.125rem);
  margin-bottom: clamp(0.5rem, 1vw, 0.75rem);
  border-left: 4px solid #48bb78;
  border: 1px solid #c6f6d5;
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: clamp(0.5rem, 1vw, 0.625rem);
`;

const CommentUser = styled.span`
  font-weight: 600;
  color: #22543d;
  font-size: clamp(0.6875rem, 0.875vw, 0.875rem);
`;

const CommentTime = styled.span`
  font-size: clamp(0.625rem, 0.75vw, 0.75rem);
  color: #64748b;
  font-weight: 500;
`;

const CommentText = styled.p`
  margin: 0;
  font-size: clamp(0.6875rem, 0.875vw, 0.875rem);
  line-height: 1.5;
  color: #2d3748;
`;

const AddCommentForm = styled.div`
  margin-top: clamp(1rem, 2vw, 1.25rem);
  padding: clamp(1rem, 2vw, 1.25rem);
  background: #f8fafc;
  border-radius: clamp(0.5rem, 1vw, 0.75rem);
  border: 1px solid #e2e8f0;
`;

const EditableContactItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: clamp(0.375rem, 0.75vw, 0.5rem) clamp(0.5rem, 1vw, 0.75rem) 0 clamp(0.5rem, 1vw, 0.75rem);
  background: rgba(255, 255, 255, 0.1);
  border-radius: clamp(0.375rem, 0.75vw, 0.5rem);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ContactActions = styled.div`
  display: flex;
  gap: clamp(0.375rem, 0.75vw, 0.5rem);
  align-items: center;
`;

const SmallButton = styled.button`
  background: ${props => {
    if (props.danger) return '#f56565';
    if (props.primary) return '#4299e1';
    return '#e2e8f0';
  }};
  color: ${props => props.danger || props.primary ? 'white' : '#4a5568'};
  border: none;
  border-radius: clamp(0.25rem, 0.5vw, 0.375rem);
  padding: clamp(0.25rem, 0.5vw, 0.375rem) clamp(0.375rem, 0.75vw, 0.5rem);
  font-size: clamp(0.625rem, 0.75vw, 0.75rem);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: clamp(0.25rem, 0.5vw, 0.375rem);
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddContactForm = styled.div`
  margin-top: clamp(0.5rem, 1vw, 0.75rem);
  padding: clamp(0.75rem, 1.5vw, 1rem);
  background: #f8fafc;
  border-radius: clamp(0.375rem, 0.75vw, 0.5rem);
  border: 1px solid #e2e8f0;
  display: flex;
  gap: clamp(0.375rem, 0.75vw, 0.5rem);
  align-items: center;
`;

const ContactInput = styled.input`
  flex: 1;
  padding: clamp(0.375rem, 0.75vw, 0.5rem) clamp(0.5rem, 1vw, 0.75rem);
  border: 1px solid #e2e8f0;
  border-radius: clamp(0.25rem, 0.5vw, 0.375rem);
  font-size: clamp(0.6875rem, 0.875vw, 0.875rem);
  
  &:focus {
    outline: none;
    border-color: #4299e1;
  }
`;

const LeadDetailView = ({ lead, onClose, onUpdate, currentUser, users = [] }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [isEditingLead, setIsEditingLead] = useState(false);
  const [editedLead, setEditedLead] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');

  // Map backend data structure to frontend expected structure
  const mapLeadData = (backendLead) => {
    if (!backendLead) return null;

    const createdDate = new Date(backendLead.created_at);

    // Map emails from backend structure to frontend structure
    const mappedEmails = (backendLead.emails || []).map(email => ({
      value: email.contact_value,
      isPrimary: Boolean(email.is_primary),
      addedBy: email.added_by,
      addedAt: email.added_at
    }));

    // Map phones from backend structure to frontend structure
    const mappedPhones = (backendLead.phones || []).map(phone => ({
      value: phone.contact_value,
      isPrimary: Boolean(phone.is_primary),
      addedBy: phone.added_by,
      addedAt: phone.added_at
    }));

    // Map comments from backend structure to frontend structure
    const mappedComments = (backendLead.comments || []).map(comment => ({
      id: comment.id,
      text: comment.comment,
      userId: comment.user_id,
      userName: comment.user_name,
      userEmployeeId: comment.user_employee_id,
      timestamp: comment.created_at
    }));

    // Map timeline from backend structure to frontend structure
    const mappedTimeline = (backendLead.timeline || []).map(item => ({
      id: item.id,
      action: item.action,
      description: item.description,
      userId: item.user_id,
      userName: item.user_name,
      userEmployeeId: item.user_employee_id,
      timestamp: item.created_at
    }));

    return {
      ...backendLead,
      // Map backend field names to frontend expected names
      name: backendLead.customer_name,
      tourName: backendLead.tour_name,
      numberOfPax: backendLead.number_of_passengers,
      travelDate: backendLead.travel_date,
      createdDate: createdDate.toLocaleDateString(),
      createdTime: createdDate.toLocaleTimeString(),
      ownerId: backendLead.owner_id,
      owner_name: backendLead.owner_name,
      owner_employee_id: backendLead.owner_employee_id,
      isDuplicate: backendLead.isDuplicate || false,
      // Use mapped arrays
      emails: mappedEmails,
      phones: mappedPhones,
      comments: mappedComments,
      timeline: mappedTimeline
    };
  };

  // Prevent body scroll when slider is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;

    // Get scrollbar width
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Prevent scroll and add padding to prevent layout shift
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, []);

  // Update editedLead when lead prop changes
  useEffect(() => {
    if (lead) {
      const mappedLead = mapLeadData(lead);
      setEditedLead(mappedLead);
      setNewStatus(mappedLead.status);
    }
  }, [lead]);

  // Handle ESC key to close slider
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onClose]);

  if (!lead) return null;
  if (!editedLead) return null;

  const source = getLeadSourceById(editedLead.source);
  const status = getStatusById(newStatus);
  const owner = editedLead.ownerId ? {
    id: editedLead.ownerId,
    fullName: editedLead.owner_name || `User ${editedLead.ownerId}`,
    employeeId: editedLead.owner_employee_id || `EMP${editedLead.ownerId.toString().padStart(3, '0')}`,
    avatar: '👤'
  } : null;

  const handleSave = async () => {
    try {
      // Prepare the update data with only the fields that can be updated
      const updateData = {};

      if (editedLead.tourName !== undefined) updateData.tour_name = editedLead.tourName;
      if (editedLead.numberOfPax !== undefined) updateData.number_of_passengers = editedLead.numberOfPax;
      if (editedLead.travelDate !== undefined) updateData.travel_date = editedLead.travelDate;
      if (editedLead.message !== undefined) updateData.message = editedLead.message;

      // Make API call to update the lead
      await apiService.updateLead(editedLead.id, updateData);

      // Refresh the lead data to show the updated information
      const refreshedLead = await apiService.getLead(editedLead.id);
      const mappedLead = mapLeadData(refreshedLead);
      setEditedLead(mappedLead);

      // Notify parent to refresh the main list
      onUpdate(refreshedLead);

      // Exit edit mode
      setIsEditing(false);

      showNotification('Travel information updated successfully');
    } catch (error) {
      console.error('Failed to update travel information:', error);
      showNotification(error.message || 'Failed to update travel information', 'error');
    }
  };

  const handleStatusChange = async (newStatusValue) => {
    setNewStatus(newStatusValue);

    try {
      await apiService.updateLeadStatus(editedLead.id, newStatusValue);

      // Refresh the lead data to show the updated status
      const refreshedLead = await apiService.getLead(editedLead.id);
      const mappedLead = mapLeadData(refreshedLead);
      setEditedLead(mappedLead);

      // Notify parent to refresh the main list
      onUpdate(refreshedLead);
    } catch (error) {
      console.error('Failed to update status:', error);
      showNotification(error.message || 'Failed to update status', 'error');
      // Revert the status if update failed
      setNewStatus(editedLead.status);
    }
  };

  const handleOwnerChange = async (newOwnerId) => {
    try {
      await apiService.assignLead(editedLead.id, newOwnerId);

      // Refresh the lead data to show the updated owner
      const refreshedLead = await apiService.getLead(editedLead.id);
      const mappedLead = mapLeadData(refreshedLead);
      setEditedLead(mappedLead);

      // Notify parent to refresh the main list
      onUpdate(refreshedLead);
    } catch (error) {
      console.error('Failed to update owner:', error);
      showNotification(error.message || 'Failed to update owner', 'error');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await apiService.addLeadComment(editedLead.id, newComment);
      setNewComment('');

      // Refresh the lead data to show the new comment
      const refreshedLead = await apiService.getLead(editedLead.id);
      const mappedLead = mapLeadData(refreshedLead);
      setEditedLead(mappedLead);

      // Notify parent to refresh the main list
      onUpdate(refreshedLead);
    } catch (error) {
      console.error('Failed to add comment:', error);
      showNotification(error.message || 'Failed to add comment', 'error');
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim()) return;

    try {
      const isPrimary = editedLead.emails.length === 0;
      await apiService.addLeadEmail(editedLead.id, newEmail, isPrimary);
      setNewEmail('');

      // Refresh the lead data to show the new email
      const refreshedLead = await apiService.getLead(editedLead.id);
      const mappedLead = mapLeadData(refreshedLead);
      setEditedLead(mappedLead);

      // Notify parent to refresh the main list
      onUpdate(refreshedLead);
    } catch (error) {
      console.error('Failed to add email:', error);
      showNotification(error.message || 'Failed to add email', 'error');
    }
  };

  const handleAddPhone = async () => {
    if (!newPhone.trim()) return;

    try {
      const isPrimary = editedLead.phones.length === 0;
      await apiService.addLeadPhone(editedLead.id, newPhone, isPrimary);
      setNewPhone('');

      // Refresh the lead data to show the new phone
      const refreshedLead = await apiService.getLead(editedLead.id);
      const mappedLead = mapLeadData(refreshedLead);
      setEditedLead(mappedLead);

      // Notify parent to refresh the main list
      onUpdate(refreshedLead);
    } catch (error) {
      console.error('Failed to add phone:', error);
      showNotification(error.message || 'Failed to add phone', 'error');
    }
  };

  const handleRemoveEmail = (index) => {
    const emailToRemove = editedLead.emails[index];
    const updatedEmails = editedLead.emails.filter((_, i) => i !== index);

    // If we removed the primary email, make the first remaining email primary
    if (emailToRemove.isPrimary && updatedEmails.length > 0) {
      updatedEmails[0].isPrimary = true;
    }

    const updatedLead = {
      ...editedLead,
      emails: updatedEmails,
      timeline: [
        ...editedLead.timeline,
        {
          id: editedLead.timeline.length + 1,
          action: 'Email Removed',
          description: `Removed email: ${emailToRemove.value}`,
          userId: currentUser?.id,
          userName: currentUser?.name,
          timestamp: new Date().toISOString()
        }
      ]
    };

    setEditedLead(updatedLead);
    onUpdate(updatedLead);
  };

  const handleRemovePhone = (index) => {
    const phoneToRemove = editedLead.phones[index];
    const updatedPhones = editedLead.phones.filter((_, i) => i !== index);

    // If we removed the primary phone, make the first remaining phone primary
    if (phoneToRemove.isPrimary && updatedPhones.length > 0) {
      updatedPhones[0].isPrimary = true;
    }

    const updatedLead = {
      ...editedLead,
      phones: updatedPhones,
      timeline: [
        ...editedLead.timeline,
        {
          id: editedLead.timeline.length + 1,
          action: 'Phone Removed',
          description: `Removed phone: ${phoneToRemove.value}`,
          userId: currentUser?.id,
          userName: currentUser?.name,
          timestamp: new Date().toISOString()
        }
      ]
    };

    setEditedLead(updatedLead);
    onUpdate(updatedLead);
  };

  const handleSetPrimaryEmail = (index) => {
    const updatedEmails = editedLead.emails.map((email, i) => ({
      ...email,
      isPrimary: i === index
    }));

    const updatedLead = {
      ...editedLead,
      emails: updatedEmails
    };

    setEditedLead(updatedLead);
    onUpdate(updatedLead);
  };

  const handleSetPrimaryPhone = (index) => {
    const updatedPhones = editedLead.phones.map((phone, i) => ({
      ...phone,
      isPrimary: i === index
    }));

    const updatedLead = {
      ...editedLead,
      phones: updatedPhones
    };

    setEditedLead(updatedLead);
    onUpdate(updatedLead);
  };

  const handleSaveContactChanges = () => {
    setIsEditingContact(false);
    onUpdate(editedLead);
  };

  const handleSaveLeadChanges = () => {
    setIsEditingLead(false);
    onUpdate(editedLead);
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const canEdit = owner?.id === currentUser?.id || currentUser?.isAdmin;

  return (
    <SliderOverlay>
      <SliderPanel onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>
            <FaUser />
            Lead Details - {lead.id}
          </Title>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
        </Header>

        <Content>
          {/* Lead Information Section */}
          <Section>
            <SectionTitle>
              <FaTag />
              Lead Information
              {canEdit && (
                <ActionButton
                  type={isEditingLead ? 'primary' : 'secondary'}
                  onClick={isEditingLead ? handleSaveLeadChanges : () => setIsEditingLead(true)}
                  style={{ marginLeft: 'auto', padding: '0.25rem 0.5rem', fontSize: '0.625rem' }}
                >
                  {isEditingLead ? <><FaSave /> Save</> : <><FaEdit /> Edit</>}
                </ActionButton>
              )}
            </SectionTitle>

            <InfoGrid>
              <InfoItem>
                <InfoLabel>Lead ID</InfoLabel>
                <InfoValue>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <strong style={{ color: '#FFD700' }}>{lead.id}</strong>
                    {lead.isDuplicate && (
                      <div style={{
                        background: '#ff6b6b20',
                        color: '#ff6b6b',
                        display: 'inline-block',
                        width: 'fit-content',
                        padding: '0.1875rem 0.375rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.625rem',
                        fontWeight: '600',
                        textAlign: 'center',
                        border: '1px solid #ff6b6b40',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.125rem'
                      }}>
                        🔄 Duplicate
                      </div>
                    )}
                  </div>
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Source</InfoLabel>
                <InfoValue>
                  <SourceBadge color={source?.color}>
                    {source?.icon} {source?.name}
                  </SourceBadge>
                </InfoValue>
              </InfoItem>

              {/* <InfoItem>
                <InfoLabel>Created Date</InfoLabel>
                <InfoValue>
                  <FaCalendarAlt />
                  {lead.createdDate}
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Created Time</InfoLabel>
                <InfoValue>
                  <FaClock />
                  {lead.createdTime}
                </InfoValue>
              </InfoItem> */}

              <InfoItem>
                <InfoLabel>Assigned Owner</InfoLabel>
                <InfoValue>
                  {isEditingLead && canEdit && currentUser?.isAdmin ? (
                    <Select
                      value={editedLead.ownerId || ''}
                      onChange={(e) => handleOwnerChange(e.target.value)}
                    >
                      <option value="">🚫 Unassigned</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          👨‍💼 {user.name} ({user.employee_id})
                        </option>
                      ))}
                    </Select>
                  ) : owner ? (
                    <div style={{
                      background: 'linear-gradient(135deg, #e6fffa, #f0fff4)',
                      padding: '0.375rem 0.625rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #68d391',
                      color: '#22543d'
                    }}>
                      <strong>{owner.avatar} {owner.fullName}</strong><br />
                      <small>Employee ID: {owner.employeeId}</small>
                    </div>
                  ) : (
                    <div style={{
                      background: 'linear-gradient(135deg, #fef3cd, #fde68a)',
                      padding: '0.375rem 0.625rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #fbbf24',
                      color: '#92400e'
                    }}>
                      🚫 Unassigned
                    </div>
                  )}
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Current Status</InfoLabel>
                <InfoValue>
                  {isEditingLead && canEdit ? (
                    <Select
                      value={newStatus}
                      onChange={(e) => handleStatusChange(e.target.value)}
                    >
                      {leadStatuses.map(status => (
                        <option key={status.id} value={status.id}>
                          {status.name}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <StatusBadge color={status?.color}>
                      {status?.icon} {status?.name}
                    </StatusBadge>
                  )}
                </InfoValue>
              </InfoItem>

            </InfoGrid>
          </Section>

          {/* Contact Information Section */}
          <Section>
            <SectionTitle>
              <FaEnvelope />
              Contact Information
              {/* Action Buttons */}
              {(canEdit) && (
                <ActionButton
                  type="secondary"
                  onClick={() => setIsEditingContact((prev) => !prev)}
                  style={{ marginLeft: 'auto', padding: '0.25rem 0.5rem', fontSize: '0.625rem' }}
                >
                  {isEditingContact ? <><FaSave /> Done</> : <><FaPlus /> Add Contact</>}
                </ActionButton>
              )}
            </SectionTitle>

            <InfoGrid>
              <InfoItem>
                <InfoLabel>Full Name</InfoLabel>
                <InfoValue>
                  <FaUser />
                  <strong>{editedLead.name}</strong>
                </InfoValue>
              </InfoItem>
            </InfoGrid>

            <InfoItemRow>
              <InfoItem style={{ marginTop: '0.5rem' }}>
                <InfoLabel>Email Addresses</InfoLabel>
                <ContactList>
                  {editedLead.emails.length > 0 ? (
                    editedLead.emails.map((email, index) => (
                      <EditableContactItem key={index}>
                        <ContactInfo>
                          {email.isPrimary && <PrimaryBadge>P</PrimaryBadge>}
                          <FaEnvelope />
                          <span>{email.value}</span>
                        </ContactInfo>
                      </EditableContactItem>
                    ))
                  ) : (
                    <div style={{
                      padding: '0.5rem',
                      color: '#64748b',
                      fontSize: '0.75rem',
                      fontStyle: 'italic',
                      textAlign: 'center',
                      background: '#f8fafc',
                      borderRadius: '0.375rem',
                      border: '1px dashed #cbd5e0'
                    }}>
                      No email address provided
                    </div>
                  )}
                </ContactList>
              </InfoItem>

              <InfoItem style={{ marginTop: '0.5rem' }}>
                <InfoLabel>Phone Numbers</InfoLabel>
                <ContactList>
                  {editedLead.phones.length > 0 ? (
                    editedLead.phones.map((phone, index) => (
                      <EditableContactItem key={index}>
                        <ContactInfo>
                          {phone.isPrimary && <PrimaryBadge>P</PrimaryBadge>}
                          <FaPhone />
                          <span>{phone.value}</span>
                          {/* whatsapp icon */}
                          <WhatsappIcon href={`https://wa.me/${phone.value}`} title='Send Whatsapp Message' target="_blank" rel="noopener noreferrer">
                            <FaWhatsapp />
                          </WhatsappIcon>
                        </ContactInfo>
                      </EditableContactItem>
                    ))
                  ) : (
                    <div style={{
                      padding: '0.5rem',
                      color: '#64748b',
                      fontSize: '0.75rem',
                      fontStyle: 'italic',
                      textAlign: 'center',
                      background: '#f8fafc',
                      borderRadius: '0.375rem',
                      border: '1px dashed #cbd5e0'
                    }}>
                      No phone numbers added yet
                    </div>
                  )}
                </ContactList>
              </InfoItem>
            </InfoItemRow>



            {/* Add Contact Inputs */}
            {isEditingContact && canEdit && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '2rem',
                marginTop: '0.75rem',
                marginBottom: '0.5rem',
                padding: '0.75rem',
                background: '#f8fafc',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ display: 'grid', flexDirection: 'column', gap: '0.25rem' }}>
                  <InfoLabel>New Email</InfoLabel>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '0.25rem' }}>
                    <EditableInput
                      type="email"
                      value={newEmail}
                      onChange={e => setNewEmail(e.target.value)}
                      placeholder="Enter new email"
                      style={{ minWidth: '10rem', width: '100%' }}
                    />
                    <ActionButton
                      type="primary"
                      onClick={handleAddEmail}
                      disabled={!newEmail.trim()}
                      style={{ fontSize: '0.625rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                    >
                      <FaEnvelope /> Add
                    </ActionButton>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <InfoLabel>New Phone</InfoLabel>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '0.25rem' }}>
                    <EditableInput
                      type="tel"
                      value={newPhone}
                      onChange={e => setNewPhone(e.target.value)}
                      placeholder="Enter new phone number"
                      style={{ minWidth: '8rem' }}
                    />
                    <ActionButton
                      type="primary"
                      onClick={handleAddPhone}
                      disabled={!newPhone.trim()}
                      style={{ fontSize: '0.625rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                    >
                      <FaPhone /> Add
                    </ActionButton>
                  </div>
                </div>
              </div>
            )}
          </Section>

          {/* Travel Information Section */}
          <Section>
            <SectionTitle>
              <FaMapMarkerAlt />
              Travel Information
              {canEdit && (
                <ActionButton
                  type={isEditing ? 'primary' : 'secondary'}
                  onClick={isEditing ? handleSave : () => setIsEditing(true)}
                  style={{ marginLeft: 'auto', padding: '0.25rem 0.5rem', fontSize: '0.625rem' }}
                >
                  {isEditing ? <><FaSave /> Save</> : <><FaEdit /> Edit</>}
                </ActionButton>
              )}
            </SectionTitle>

            <InfoGrid>
              <InfoItem style={{ gridColumn: 'span 2' }}>
                <InfoLabel>Tour/Destination</InfoLabel>
                <InfoValue>
                  {isEditing && canEdit ? (
                    <EditableInput
                      value={editedLead.tourName}
                      onChange={(e) => setEditedLead(prev => ({ ...prev, tourName: e.target.value }))}
                      placeholder="Tour/Destination"
                    />
                  ) : (
                    <>
                      <FaMapMarkerAlt />
                      {editedLead.tourName}
                    </>
                  )}
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Number of Passengers</InfoLabel>
                <InfoValue>
                  {isEditing && canEdit ? (
                    <EditableInput
                      type="number"
                      value={editedLead.numberOfPax}
                      onChange={(e) => setEditedLead(prev => ({ ...prev, numberOfPax: parseInt(e.target.value) }))}
                      placeholder="Number of passengers"
                    />
                  ) : (
                    <>
                      <FaUsers />
                      {editedLead.numberOfPax} passenger{editedLead.numberOfPax !== 1 ? 's' : ''}
                    </>
                  )}
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Travel Date</InfoLabel>
                <InfoValue>
                  {isEditing && canEdit ? (
                    <EditableInput
                      type="date"
                      value={editedLead.travelDate}
                      onChange={(e) => setEditedLead(prev => ({ ...prev, travelDate: e.target.value }))}
                    />
                  ) : (
                    <>
                      <FaCalendarAlt />
                      {editedLead.travelDate}
                    </>
                  )}
                </InfoValue>
              </InfoItem>
            </InfoGrid>

            <InfoItem style={{ marginTop: '0.9375rem' }}>
              <InfoLabel>Message/Requirements</InfoLabel>
              <InfoValue>
                {isEditing && canEdit ? (
                  <EditableTextArea
                    value={editedLead.message}
                    onChange={(e) => setEditedLead(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Customer message or requirements"
                  />
                ) : (
                  <p style={{
                    margin: 0,
                    padding: '0.625rem',
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '0.5rem',
                    lineHeight: '1.4'
                  }}>
                    {editedLead.message}
                  </p>
                )}
              </InfoValue>
            </InfoItem>
          </Section>

          {/* Comments Section */}
          <Section>
            <SectionTitle>
              <FaComment />
              Comments & Notes
            </SectionTitle>

            {editedLead.comments.length > 0 && (
              <CommentSection>
                {editedLead.comments.map(comment => (
                  <CommentItem key={comment.id}>
                    <CommentHeader>
                      <CommentUser>{comment.userName} {comment.userEmployeeId && `(${comment.userEmployeeId})`}</CommentUser>
                      <CommentTime>{formatDateTime(comment.timestamp)}</CommentTime>
                    </CommentHeader>
                    <CommentText>{comment.text}</CommentText>
                  </CommentItem>
                ))}
              </CommentSection>
            )}

            {canEdit && (
              <AddCommentForm>
                <InfoLabel>Add New Comment</InfoLabel>
                <EditableTextArea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Enter your comment or note..."
                />
                <ActionButtons>
                  <ActionButton type="primary" onClick={handleAddComment} disabled={!newComment.trim()}>
                    <FaComment /> Add Comment
                  </ActionButton>
                </ActionButtons>
              </AddCommentForm>
            )}
          </Section>

          {/* Activity Timeline Section */}
          <Section>
            <SectionTitle>
              <FaHistory />
              Activity Timeline
            </SectionTitle>

            <Timeline>
              {[...editedLead.timeline].reverse().map((item, index) => (
                <TimelineItem key={item.id}>
                  <TimelineIcon>
                    {index + 1}
                  </TimelineIcon>
                  <TimelineContent>
                    <TimelineHeader>
                      <TimelineAction>{item.action}</TimelineAction>
                      <TimelineTime>{formatDateTime(item.timestamp)}</TimelineTime>
                    </TimelineHeader>
                    <TimelineDescription>{item.description}</TimelineDescription>
                    {item.userEmployeeId ? <TimelineUser>
                      By: {item.userName} {item.userEmployeeId && `(${item.userEmployeeId})`}
                    </TimelineUser> : <></>}
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </Section>
        </Content>
      </SliderPanel>
    </SliderOverlay>
  );
};

export default LeadDetailView; 